import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  useTheme,
  Box,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import QualificationDetailDialog, { STATUS_COLOR } from './QualificationDetailDialog';


const STATUS_BG = {
  valid:           'rgba(56,142,60,0.15)',
  'expiring-soon': 'rgba(245,124,0,0.18)',
  expired:         'rgba(211,47,47,0.18)',
  missing:         null,
};

const CellContent = ({ qual }) => {
  if (!qual || qual.status === 'missing') {
    return <Typography variant="caption" color="text.disabled">-</Typography>;
  }
  return (
    <Box textAlign="center">
      <Chip
        label={qual.status === 'expiring-soon' ? 'Soon' : qual.status === 'expired' ? 'Exp' : 'OK'}
        color={STATUS_COLOR[qual.status]}
        size="small"
        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }}
      />
      {qual.expiryDate && (
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {format(new Date(qual.expiryDate), 'MM/yy')}
        </Typography>
      )}
    </Box>
  );
};

const QualificationMatrix = ({ matrixData }) => {
  const theme = useTheme();
  const [dialog, setDialog] = useState({ open: false, employee: null, subject: null, qual: null });

  const sorted = useMemo(() => {
    if (!matrixData?.employees) return [];
    return [...matrixData.employees].sort(
      (a, b) => Number(a.employeeCode) - Number(b.employeeCode)
    );
  }, [matrixData]);

  const subjects = matrixData?.subjects || [];

  if (!subjects.length && !sorted.length) {
    return (
      <Typography color="text.secondary" p={2}>
        No data available. Adjust filters or check that sessions have been saved with results.
      </Typography>
    );
  }

  const handleCellClick = (employee, subject) => {
    const sid = subject._id.toString();
    const qual = employee.qualifications?.[sid] || null;
    setDialog({ open: true, employee, subject, qual });
  };

  return (
    <>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                position: 'sticky', 
                left: 0, 
                zIndex: 3,
                bgcolor: 'background.paper',
                fontWeight: 'bold', 
                fontSize: 12, 
                borderBottomWidth: 2,
                width: 36, 
                minWidth: 36, 
                maxWidth: 36,
              }}>
                #
              </TableCell>
              <TableCell sx={{
                position: 'sticky', 
                left: 36, 
                zIndex: 3,
                bgcolor: 'background.paper',
                fontWeight: 'bold', 
                fontSize: 12, 
                borderBottomWidth: 2,
                minWidth: { xs: 150, md: 200 },
              }}>
                Employee
              </TableCell>
              {subjects.map((s) => (
                <TableCell key={s._id} align="center" sx={{
                  fontWeight: 'bold', fontSize: 11, borderBottomWidth: 2,
                  whiteSpace: 'nowrap', minWidth: 72,
                }}>
                  <Tooltip title={s.subjectName} placement="top" arrow>
                    <span>{s.subjectCode}</span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((emp, idx) => (
              <TableRow key={emp.employeeCode} hover>
                <TableCell sx={{
                  position: 'sticky', 
                  left: 0, 
                  zIndex: 1,
                  bgcolor: 'background.paper', 
                  fontSize: 12, 
                  py: 0.75,
                  width: 36, 
                  minWidth: 36, 
                  maxWidth: 36,
                }}>
                  {idx + 1}
                </TableCell>
                <TableCell sx={{
                  position: 'sticky', 
                  left: 36, 
                  zIndex: 1,
                  bgcolor: 'background.paper', 
                  py: 0.75,
                }}>
                  <Box 
                    display="flex" 
                    flexWrap="nowrap"
                    alignItems="baseline" 
                    gap={0.75} 
                  >
                    <Typography variant="body2" flexShrink={0}>
                      {emp.employeeCode}
                    </Typography>
                    {emp.employeeName && (
                      <Typography variant="body2" noWrap>
                        {emp.employeeName}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap={0.5} mt={0.25}>
                    {emp.rank && (
                      <Chip 
                        variant="outlined"
                        size="small" 
                        label={emp.rank} 
                        sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
                    )}
                    {emp.base && (
                      <Typography variant="caption" color="text.disabled">
                        {emp.base}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {subjects.map((s) => {
                  const sid = s._id.toString();
                  const qual = emp.qualifications?.[sid];
                  const bg = STATUS_BG[qual?.status] || null;
                  return (
                    <TableCell
                      key={sid}
                      align="center"
                      onClick={() => handleCellClick(emp, s)}
                      sx={{
                        cursor: 'pointer', 
                        py: 0.5, 
                        px: 0.5,
                        bgcolor: bg || undefined,
                        '&:hover': { bgcolor: theme.palette.action.hover },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <CellContent qual={qual} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <QualificationDetailDialog
        open={dialog.open}
        onClose={() => setDialog({ 
          open: false, 
          employee: null, 
          subject: null, 
          qual: null 
        })}
        employee={dialog.employee}
        subject={dialog.subject}
        qual={dialog.qual}
      />
    </>
  );
};

export default QualificationMatrix;
